import { useSessionStore, type Role } from '@/stores/useSessionStore'
import { mapIdentityRoleToSensemaker } from './map-identity-role'
import { getPlatformAdminSubdomain } from './platform-admin-host'

/** First DNS label when the browser is on a tenant host (e.g. `acme.localhost` → `acme`). */
export function getSubdomainLabelFromHostname(hostname: string): string | null {
  const parts = hostname.split('.')
  if (parts.length < 2) return null
  const first = parts[0]
  if (!first || first === 'localhost') return null
  return first
}

type MeSubdomainSource = {
  subdomain?: string | null
  tenantSubdomain?: string | null
  role?: string
  permissions?: string[]
}

/**
 * Resolve tenant DNS label for outbound API calls and session storage.
 * Identity `/me` uses `subdomain`; login `loggedUser` uses `tenantSubdomain`.
 * Platform admins have no tenant subdomain — always null.
 */
export function resolveMeSubdomain(
  raw: MeSubdomainSource,
  sessionRole?: Role | null,
): string | null {
  const mappedRole =
    sessionRole ?? mapIdentityRoleToSensemaker(raw.role ?? '', raw.permissions)
  if (mappedRole === 'PLATFORM_ADMIN') {
    return null
  }

  const fromApi =
    raw.subdomain?.trim() || raw.tenantSubdomain?.trim() || ''
  return fromApi.length > 0 ? fromApi : null
}
/**
 * Subdomain for `x-subdomain` headers and Socket.io auth.
 * Prefer the host label; on the auth apex fall back to the session user
 * (Platform Admin → `PLATFORM_ADMIN_SUBDOMAIN` / `admin`).
 */
export function resolveOutboundSubdomain(hostname: string): string | null {
  const fromHost = getSubdomainLabelFromHostname(hostname)
  if (fromHost) return fromHost

  const user = useSessionStore.getState().user
  if (user?.role === 'PLATFORM_ADMIN') {
    return getPlatformAdminSubdomain()
  }

  const sessionSub = user?.subdomain?.trim()
  return sessionSub && sessionSub.length > 0 ? sessionSub : null
}
